import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { filter, from, merge, pipe, switchMap, tap } from 'rxjs';
import { GroupService } from '@thiememeulenhoff/ng-api-gateway';
import { Group } from '@thiememeulenhoff/types';
import { UserSettingStore } from '../user-setting';
import {
  RequestStatus,
  withRequestStatus,
} from '../_utils/request-status.feature';

type GroupState = {
  groups: Array<Group>;
  selectedGroupId: string;
  arrangementCode: string | null;
  methodSlug: string | null;
  fetchOnlyMyGroups: boolean;
};

const initialState: GroupState = {
  groups: [],
  selectedGroupId: '',
  arrangementCode: null,
  methodSlug: null,
  fetchOnlyMyGroups: false,
};

export const GroupStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withRequestStatus(),
  withComputed(({ groups, selectedGroupId }) => ({
    selectedGroup: computed(() =>
      groups().find((g) => g.id === selectedGroupId()),
    ),
    validatedSelectedGroupId: computed(() => {
      const selected = String(selectedGroupId());
      return String(groups().find((g) => String(g.id) === selected)?.id ?? '');
    }),
  })),
  withMethods(
    (
      store,
      groupService = inject(GroupService),
      userSettingStore = inject(UserSettingStore),
    ) => {
      const fetchGroupsFromService = () =>
        groupService
          .fetchGroups(
            store.methodSlug() ?? '',
            false,
            false,
            store.fetchOnlyMyGroups(),
          )
          .pipe(
            tap(() => patchState(store, { requestStatus: 'loading' })),
            tapResponse({
              next: (groupsIndex) =>
                patchState(store, {
                  groups: groupsIndex.items,
                  requestStatus: 'success',
                }),
              error: (error: Error) =>
                patchState(store, {
                  requestStatus: {
                    error: error.message || 'Failed to fetch',
                  },
                }),
            }),
          );

      const fetchGroups = switchMap(() =>
        merge(
          fetchGroupsFromService(),
          from(
            userSettingStore.getUserSetting(
              `selected-group-${store.arrangementCode()}`,
            ),
          ).pipe(
            tapResponse({
              next: (setting) => {
                patchState(store, {
                  selectedGroupId: setting?.value ?? '',
                  requestStatus: 'success',
                });
              },
              error: (error: Error) =>
                patchState(store, {
                  requestStatus: {
                    error: error.message || 'Failed to fetch',
                  },
                }),
            }),
          ),
        ),
      );

      return {
        fetchGroup: rxMethod<string>(
          pipe(
            tap(() => patchState(store, { requestStatus: 'loading' })),
            switchMap((id) =>
              groupService.fetchGroup(id).pipe(
                tapResponse({
                  next: (group) =>
                    patchState(store, (state) => ({
                      groups: state.groups.some((g) => g.id === group.id)
                        ? state.groups.map((g) =>
                            g.id === group.id ? group : g,
                          )
                        : [...state.groups, group],
                      requestStatus: 'success' as RequestStatus,
                    })),
                  error: (error: Error) =>
                    patchState(store, {
                      requestStatus: {
                        error: error.message || 'Failed to fetch',
                      },
                    }),
                }),
              ),
            ),
          ),
        ),
        setFetchOnlyMyGroupsAndFetch: rxMethod<boolean>(
          pipe(
            filter(
              (fetchOnlyMyGroups) =>
                store.fetchOnlyMyGroups() !== fetchOnlyMyGroups,
            ),
            tap((fetchOnlyMyGroups) =>
              patchState(store, {
                fetchOnlyMyGroups,
                requestStatus: 'loading',
              }),
            ),
            fetchGroups,
          ),
        ),
        setArrangementCodeAndMethodSlugAndFetch: rxMethod<{
          arrangementCode: string;
          methodSlug: string;
        }>(
          pipe(
            filter(
              ({ arrangementCode, methodSlug }) =>
                store.arrangementCode() !== arrangementCode ||
                store.methodSlug() !== methodSlug,
            ),
            tap(({ arrangementCode, methodSlug }) =>
              patchState(store, {
                arrangementCode,
                methodSlug,
                requestStatus: 'loading',
              }),
            ),
            fetchGroups,
          ),
        ),
        selectGroup: rxMethod<string>(
          pipe(
            filter(
              (id) =>
                store.arrangementCode() !== null &&
                store.selectedGroupId() !== id,
            ),
            tap((id) => patchState(store, { selectedGroupId: id })),
            switchMap((id) =>
              from(
                userSettingStore.setUserSetting(
                  {
                    key: `selected-group-${store.arrangementCode()}`,
                    value: id,
                  },
                  true,
                ),
              ).pipe(
                tapResponse({
                  next: () => patchState(store, { requestStatus: 'success' }),
                  error: (error: Error) =>
                    patchState(store, {
                      requestStatus: {
                        error: error.message || 'Failed to select group',
                      },
                    }),
                }),
              ),
            ),
          ),
        ),
      };
    },
  ),
);
